import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import MovieCard from "@/components/base/MovieCard";
import SkeletonCard from "@/components/base/SkeletonCard";
import FilterBar, { type FilterState } from "@/components/base/FilterBar";
import Pagination from "@/components/feature/Pagination";
import { movies } from "@/mocks/movies";
import { categories } from "@/mocks/categories";
import {
  filterByCriteria,
  getAllDecades,
  getAllDirectors,
  getAllGenres,
  searchMovies,
  sortMovies,
} from "@/utils/movieUtils";
import type { SortKey } from "@/types";

const PAGE_SIZE = 6;

const emptyFilters: FilterState = { keyword: "", genres: [], directors: [], decades: [] };

export default function CategoryPage() {
  const { type } = useParams<{ type: string }>();
  const category = categories.find((item) => item.key === type);

  const [filters, setFilters] = useState<FilterState>(emptyFilters);
  const [sort, setSort] = useState<SortKey>("popular");
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);

  const baseMovies = useMemo(
    () => (category ? filterByCriteria(movies, category.criteria) : []),
    [category],
  );

  useEffect(() => {
    setLoading(true);
    setFilters(emptyFilters);
    setSort("popular");
    setPage(1);
    const timer = window.setTimeout(() => setLoading(false), 450);
    return () => window.clearTimeout(timer);
  }, [type]);

  const filteredMovies = useMemo(() => {
    let result = baseMovies;
    if (filters.keyword.trim()) {
      result = searchMovies(result, filters.keyword);
    }
    if (filters.decades.length > 0) {
      result = result.filter((movie) => filters.decades.includes(movie.decade));
    }
    if (filters.genres.length > 0) {
      result = result.filter((movie) => movie.genre.some((genre) => filters.genres.includes(genre)));
    }
    if (filters.directors.length > 0) {
      result = result.filter((movie) => filters.directors.includes(movie.director));
    }
    return sortMovies(result, sort);
  }, [baseMovies, filters, sort]);

  const totalPages = Math.max(1, Math.ceil(filteredMovies.length / PAGE_SIZE));
  const visibleMovies = filteredMovies.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const handleFilterChange = (next: FilterState) => {
    setFilters(next);
    setPage(1);
  };

  if (!category) {
    return (
      <div className="mx-auto flex w-full max-w-3xl flex-col items-center gap-4 px-4 py-24 text-center md:px-6">
        <span className="flex h-16 w-16 items-center justify-center rounded-full bg-primary-100 text-primary-600">
          <i className="ri-folder-unknow-line text-3xl" />
        </span>
        <h1 className="font-heading text-2xl font-bold text-foreground-950">未找到该分类</h1>
        <p className="text-sm text-foreground-600">你访问的分类不存在或已被调整，请返回首页重新选择。</p>
        <Link
          to="/"
          className="inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
        >
          <i className="ri-home-4-line" />
          返回首页
        </Link>
      </div>
    );
  }

  return (
    <div className="w-full px-4 py-8 md:px-6 md:py-10">
      <nav className="flex flex-wrap items-center gap-2 text-xs text-foreground-500" aria-label="面包屑">
        <Link to="/" className="cursor-pointer transition-colors hover:text-primary-600">首页</Link>
        <i className="ri-arrow-right-s-line text-foreground-300" />
        <span>分类</span>
        <i className="ri-arrow-right-s-line text-foreground-300" />
        <span className="text-foreground-800">{category.name}</span>
      </nav>

      <header className="mt-6 flex flex-col gap-3 rounded-lg border border-background-300/70 bg-background-50 p-6 md:p-8">
        <div className="flex items-center gap-3">
          <span className="flex h-12 w-12 items-center justify-center rounded-lg bg-primary-100 text-primary-600">
            <i className={`${category.icon} text-2xl`} />
          </span>
          <div>
            <span className="text-xs uppercase tracking-wide text-foreground-400">{category.group}</span>
            <h1 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">{category.name} 电影分类</h1>
          </div>
        </div>
        <p className="max-w-3xl text-sm leading-relaxed text-foreground-600">{category.description}</p>
        <p className="text-xs text-foreground-500">共收录 {baseMovies.length} 部影片</p>
      </header>

      <div className="mt-6">
        <FilterBar
          options={{
            genres: getAllGenres(baseMovies),
            directors: getAllDirectors(baseMovies),
            decades: getAllDecades(baseMovies),
          }}
          value={filters}
          onChange={handleFilterChange}
          sort={sort}
          onSortChange={(next) => {
            setSort(next);
            setPage(1);
          }}
          onReset={() => handleFilterChange(emptyFilters)}
        />
      </div>

      {loading ? (
        <div className="mt-6 grid grid-cols-2 gap-4 md:grid-cols-3">
          {Array.from({ length: PAGE_SIZE }).map((_, index) => (
            <SkeletonCard key={index} />
          ))}
        </div>
      ) : filteredMovies.length === 0 ? (
        <div className="mt-6 flex flex-col items-center gap-4 rounded-lg border border-background-300/70 bg-background-50 px-6 py-16 text-center">
          <span className="flex h-16 w-16 items-center justify-center rounded-full bg-secondary-100 text-secondary-600">
            <i className="ri-search-eye-line text-3xl" />
          </span>
          <h2 className="font-heading text-lg font-bold text-foreground-950">暂无相关影片</h2>
          <p className="max-w-md text-sm text-foreground-600">
            当前筛选条件下没有匹配的电影，试试更换关键词或重置筛选条件。
          </p>
          <button
            type="button"
            onClick={() => handleFilterChange(emptyFilters)}
            className="cursor-pointer whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
          >
            重置筛选条件
          </button>
        </div>
      ) : (
        <>
          <div className="mt-6 grid grid-cols-2 gap-4 md:grid-cols-3">
            {visibleMovies.map((movie, index) => (
              <MovieCard key={movie.id} movie={movie} index={index} />
            ))}
          </div>
          <Pagination page={page} totalPages={totalPages} onChange={setPage} />
        </>
      )}
    </div>
  );
}